/**
 * eclipse — receiver-side eclipses, the PURE mirror (shader gaps R4, 2026-09-30 — the owner's report §6.4).
 *
 * Before this, an eclipse was a CAMERA effect only: `space_look.eclipseVisible` faded the sun's GLARE by what covers
 * the disc as seen from the camera, while a ship parked in a planet's shadow — or a planet under another body's
 * shadow — stayed lit as if nothing were in the way. Here the visibility of the sun is evaluated AT THE RECEIVER (a
 * hull fragment, a planet's surface, a point of a ring or a cloud shell), independent of where the camera looks
 * (Schneegans, Gilg, Ahlers & Gerndt, "Real-Time Rendering of Eclipses without Incorporation of Atmospheric Effects",
 * CGF 41(7), Pacific Graphics 2022 — the AIRLESS model; their atmospheric follow-up, "Physically Based Real-Time
 * Rendering of Eclipses", CGF 2025, with the coloured refracted light of a lunar eclipse, is a later phase):
 *
 *  - Seen from the receiver, the sun is a disc of angular radius α_s = asin(R_sun/d_sun) (sys0: the fixed game sun,
 *    `SYS0_SUN_ANG_R`) and each occluder a disc α_o = asin(R/d); their centres are δ apart. The flat CIRCLE-intersection
 *    area is what CosmoScout VR evaluates (≤ 0.08 % from the exact spherical caps at Mercury; δ = 2·asin(½|u − v|),
 *    their small-angle-safe angle). Our game sun is 0.55°, so the planar form is exact to far better than a pixel.
 *  - LIMB DARKENING: the sun is not a uniform disc (the penumbra's brightness profile is wrong without it — the paper's
 *    central point). The disc is split into `LD_K` annuli at equal steps of μ (where the law changes fastest, the
 *    limb) with each annulus's EXACT mean intensity under `space_look.limbDarkening` (the quadratic law 0.30 + 0.94μ −
 *    0.24μ² the sun disc is drawn with); the blocked flux is Σ c_k · overlap(ρ_k, α_o, δ) (Abel summation) — the same
 *    `discOverlapArea` the glare uses, K times. The TSL evaluates the identical sum (`space_light_look.ts`).
 *    Schneegans's shadow-map LUT (their `x = α_s/(α_s + α_o)`, `y = δ/(α_s + α_o)`) was not used: a ship 6.5 km over the
 *    78 km Commons sees an occluder 120× the sun, which puts the whole penumbra in the top 1.7 % of the LUT's y — the
 *    analytic annuli are exact at any size ratio and cost nothing outside a penumbra (the shader early-outs).
 *  - Independent occluders compose as a PRODUCT (two moons over one sun never read as "more than eclipsed"), times the
 *    ring's transmittance (`rings.ringShadowT`) when a ring lies between the receiver and the sun.
 *  - RELEVANCE: a receiver never loops over every mesh — `pickOccluders` keeps the few whose shadow can reach it (the
 *    angular separation within α_s + α_o + the receiver's own extent), strongest first.
 *
 * ONE VISIBILITY: the same number multiplies the direct sun, the specular / ocean glint and the atmosphere's sun term of
 * that receiver. The deliberate FILL (the starlight floor, planetshine, the IBL's near-black floor) is not eclipsed —
 * it is what an eclipsed hull still sees; the IBL's warm SUN lobe is (scaled by the ship's visibility — one texture).
 *
 * NO-CYCLE RULE: numbers in, numbers out (three-free).
 */
import { discOverlapArea, limbDarkening, angularRadius, type V3 } from './space_look.js';
import { ringShadowT, type RingProfile, type RingSpec } from './rings.js';

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

// ── the limb-darkened sun as K annuli ────────────────────────────────────────────────────────────────────────────
/** The quadratic limb law's coefficients (space_look.limbDarkening: I(μ) = LD_A + LD_B·μ + LD_C·μ²). */
export const LD_A = 0.30, LD_B = 0.94, LD_C = -0.24;
/** Annuli the disc is split into (equal steps of μ). The harness bounds the error against a fine integral. */
export const LD_K = 4;
/** ∫ I(μ)·μ dμ (the area weight of an annulus in μ: dA = 2π r dr = −2π μ dμ). */
const ldMoment = (m: number): number => (LD_A * m * m) / 2 + (LD_B * m * m * m) / 3 + (LD_C * m * m * m * m) / 4;
/** The annuli's outer radii ρ_k (fractions of the disc) and their Abel coefficients c_k (Σ c_k·π ρ_k² = 1). */
export const LD_RHO: number[] = [];
export const LD_COEF: number[] = [];
(() => {
  const I: number[] = [];
  let flux = 0, prevRho2 = 0;
  for (let k = 1; k <= LD_K; k++) {
    const mHi = 1 - (k - 1) / LD_K, mLo = 1 - k / LD_K; // μ runs 1 (centre) → 0 (limb)
    const rho = Math.sqrt(1 - mLo * mLo);
    const mean = (ldMoment(mHi) - ldMoment(mLo)) / ((mHi * mHi - mLo * mLo) / 2);
    LD_RHO.push(rho); I.push(mean);
    flux += mean * (rho * rho - prevRho2); prevRho2 = rho * rho;
  }
  for (let k = 0; k < LD_K; k++) LD_COEF.push((I[k] - (k + 1 < LD_K ? I[k + 1] : 0)) / (Math.PI * flux));
})();

/** The angle between two unit vectors, small-angle safe (CosmoScout's 2·asin(½|u − v|)). */
export function angleBetween(u: V3, v: V3): number {
  const dx = u.x - v.x, dy = u.y - v.y, dz = u.z - v.z;
  return 2 * Math.asin(clamp(0.5 * Math.hypot(dx, dy, dz), 0, 1));
}

/**
 * The VISIBLE fraction of a limb-darkened sun (angular radius aS) behind one occluder (aO) δ apart — the K-annulus sum.
 * 1 = in full sun, 0 = total. The shader's mirror.
 */
export function visibleFraction(aS: number, aO: number, d: number): number {
  if (!(aS > 0) || !(aO > 0)) return 1;
  const o = aO / aS, dd = d / aS;
  if (dd >= 1 + o) return 1;
  if (o >= 1 && dd <= o - 1) return 0; // TOTAL: the sun inside the occluder's disc — exactly 0, never a rounding residue
  let blocked = 0;
  for (let k = 0; k < LD_K; k++) blocked += LD_COEF[k] * discOverlapArea(LD_RHO[k], o, dd);
  return clamp(1 - blocked, 0, 1);
}
/** The uniform-disc (no limb darkening) visible fraction — what the glare model and CosmoScout's mode 4 compute. */
export function visibleFractionUniform(aS: number, aO: number, d: number): number {
  if (!(aS > 0) || !(aO > 0)) return 1;
  return clamp(1 - discOverlapArea(aS, aO, d) / (Math.PI * aS * aS), 0, 1);
}
/** Ground truth: the limb-darkened disc integrated on a fine polar grid (the harness's oracle; n radial rings). */
export function visibleFractionTruth(aS: number, aO: number, d: number, n = 256): number {
  if (!(aS > 0) || !(aO > 0)) return 1;
  const o = aO / aS, dd = d / aS;
  let tot = 0, vis = 0;
  const m = n * 4;
  for (let i = 0; i < n; i++) {
    const r = (i + 0.5) / n, w = limbDarkening(r) * r;
    for (let j = 0; j < m; j++) {
      const a = ((j + 0.5) / m) * 2 * Math.PI;
      const x = r * Math.cos(a) - dd, y = r * Math.sin(a);
      tot += w;
      if (x * x + y * y >= o * o) vis += w;
    }
  }
  return tot > 0 ? vis / tot : 1;
}

// ── the sun and the occluders, seen from a receiver ──────────────────────────────────────────────────────────────
/** sys0's sun sits at infinity along a direction; a derived system's star is a sphere at a position (same frame). */
export type EclipseSun = { kind: 'dir'; dir: V3; angR: number } | { kind: 'point'; pos: V3; radius: number };
export interface Occluder { x: number; y: number; z: number; r: number; key?: string }

export function sunFrom(P: V3, sun: EclipseSun): { dir: V3; angR: number; dist: number } {
  if (sun.kind === 'dir') return { dir: sun.dir, angR: sun.angR, dist: Infinity };
  const dx = sun.pos.x - P.x, dy = sun.pos.y - P.y, dz = sun.pos.z - P.z;
  const dist = Math.max(1, Math.hypot(dx, dy, dz));
  return { dir: { x: dx / dist, y: dy / dist, z: dz / dist }, angR: angularRadius(sun.radius, dist), dist };
}
/** One occluder's visible fraction at P (1 when it is behind the star, 0 when P is inside it). */
export function occluderVisibility(P: V3, s: { dir: V3; angR: number; dist: number }, o: Occluder): number {
  if (!(o.r > 0)) return 1;
  const dx = o.x - P.x, dy = o.y - P.y, dz = o.z - P.z;
  const d = Math.hypot(dx, dy, dz);
  if (d <= o.r) return 0;
  if (d > s.dist) return 1;
  const dir = { x: dx / d, y: dy / d, z: dz / d };
  return visibleFraction(s.angR, Math.asin(o.r / d), angleBetween(s.dir, dir));
}
/**
 * THE RECEIVER'S VISIBILITY of the sun at P (1 = full sun): the product of every occluder's visible fraction, times the
 * ring's transmittance when a ring spec + profile are given. Where the camera stands never enters.
 */
export function receiverVisibility(P: V3, sun: EclipseSun, occluders: ReadonlyArray<Occluder>, ring?: { spec: RingSpec; prof: RingProfile } | null): number {
  const s = sunFrom(P, sun);
  let vis = 1;
  for (const o of occluders) { vis *= occluderVisibility(P, s, o); if (vis <= 0) return 0; }
  if (ring) vis *= ringShadowT(P, s.dir, s.angR, ring.spec, ring.prof);
  return clamp(vis, 0, 1);
}

/**
 * RELEVANCE: the occluders whose shadow can touch a receiver region (a sphere of `reach` m around P0), strongest
 * first, at most `max`. An occluder qualifies when the angular separation of its centre from the sun, seen from P0,
 * is under α_s + α_o + (reach/d) — the receiver's own extent seen from it — and it lies between P0 and the star.
 * `skip` excludes the receiver body itself.
 */
export function pickOccluders(P0: V3, reach: number, sun: EclipseSun, candidates: ReadonlyArray<Occluder>, max: number, skip?: string): Occluder[] {
  const s = sunFrom(P0, sun);
  const scored: Array<{ o: Occluder; margin: number }> = [];
  for (const o of candidates) {
    if (!(o.r > 0) || (skip !== undefined && o.key === skip)) continue;
    const dx = o.x - P0.x, dy = o.y - P0.y, dz = o.z - P0.z;
    const d = Math.hypot(dx, dy, dz);
    if (d > s.dist || d <= 1) continue;
    if (d < o.r + reach) { scored.push({ o, margin: 10 }); continue; } // the region touches the body: always relevant
    const aO = Math.asin(Math.min(1, o.r / d));
    const sep = angleBetween(s.dir, { x: dx / d, y: dy / d, z: dz / d });
    const lim = s.angR + aO + Math.asin(Math.min(1, reach / d));
    if (sep < lim) scored.push({ o, margin: (lim - sep) / Math.max(1e-9, lim) });
  }
  scored.sort((a, b) => b.margin - a.margin);
  return scored.slice(0, max).map((x) => x.o);
}

/** The eclipse regime at P for one occluder (the harness's words): none / partial / annular / total. */
export function eclipseKind(aS: number, aO: number, d: number): 'none' | 'partial' | 'annular' | 'total' {
  if (d >= aS + aO) return 'none';
  if (aO >= aS && d <= aO - aS) return 'total';
  if (aO < aS && d <= aS - aO) return 'annular';
  return 'partial';
}
/** The penumbra's width (m) at distance D behind an occluder (both edges of the sun: 2·D·α_s, +2R·… is the umbra). */
export function penumbraWidthM(D: number, aS: number): number { return 2 * D * aS; }
/** How long the umbra reaches behind an occluder of radius R (m) under a sun α_s wide (∞ when the occluder is bigger). */
export function umbraLengthM(R: number, aS: number): number { return aS > 0 ? R / Math.tan(aS) : Infinity; }
